"use client";

import { useRef, useState, useEffect } from "react";
import { SendButton } from "@ui/SendButton";
import { ConvoIcon } from "@ui/ConvoIcon";
import { useLessonPreferences } from "@components/lesson/LessonPreferencesProvider";
import { useTranslations } from "@i18n/provider";
import { useAgentOverlayContext } from "./AgentOverlayProvider";
import { AgentActionChip } from "./AgentActionChip";
import { AgentChatDebugPanel } from "./AgentChatDebugPanel";
import { TutorMemoryPanel } from "./TutorMemoryPanel";
import { HistoryIcon } from "@ui/HistoryIcon";
import { NewChatIcon } from "@ui/NewChatIcon";
import { TutorSessionsPanel } from "./TutorSessionsPanel";
import { useTutorSessions } from "./useTutorSessions";
import {
  subscribeLessonAudioPlay,
  subscribeLessonAudioStop,
  stopLessonAudio,
} from "@lesson/shared/lessonAudio";

const isDev = process.env.NODE_ENV === "development";
type WidgetTab = "chat" | "debug";

const MAX_INPUT_HEIGHT = 140;

const CORAL_HEADER_STYLE = {
  background: "linear-gradient(135deg, var(--coral-light) 0%, var(--coral) 100%)",
};

const LAUNCHER_HAZE_VISIBLE =
  "drop-shadow(0 8px 20px rgba(201, 106, 69, 0.45)) drop-shadow(0 1px 3px rgba(0,0,0,0.08))";
const LAUNCHER_HAZE_HIDDEN =
  "drop-shadow(0 8px 20px rgba(201, 106, 69, 0)) drop-shadow(0 1px 3px rgba(0,0,0,0))";

// Touch-sized on the phone sheet, tighter on the narrow desktop panel where
// three icons plus the close button have to share the header with the title.
const HEADER_ICON_BUTTON =
  "flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full p-1.5 transition-colors hover:bg-white/10 sm:h-9 sm:w-9 sm:min-h-0 sm:min-w-0";

export function AgentChatWidget({
  hasTutorMemory: hasTutorMemoryProp,
}: { hasTutorMemory?: boolean } = {}) {
  const { state, sendUserMessage, dismiss, open } = useAgentOverlayContext();
  const { t } = useTranslations();
  const { messages, isOpen, nudgeAt, mode } = state;
  const { reducedMotion } = useLessonPreferences();
  const [input, setInput] = useState("");
  const [tab, setTab] = useState<WidgetTab>("chat");
  const [isNudgeSpinning, setIsNudgeSpinning] = useState(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [memoryPanelOpen, setMemoryPanelOpen] = useState(false);
  const [sessionsPanelOpen, setSessionsPanelOpen] = useState(false);
  const { sessions, activeSessionId, newSession, switchSession } = useTutorSessions();
  const [fetchedHasTutorMemory, setFetchedHasTutorMemory] = useState(false);
  const hasTutorMemory = hasTutorMemoryProp ?? fetchedHasTutorMemory;
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const lastNudgeAtRef = useRef(0);
  const audioEndTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isTutorMode = mode.type === "tutor";

  useEffect(() => {
    if (hasTutorMemoryProp !== undefined) return;
    let cancelled = false;
    void fetch("/api/tutor-memory/brief")
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { brief?: string; items?: unknown[] } | null) => {
        if (cancelled || !data) return;
        if (data.brief || (data.items?.length ?? 0) > 0) {
          setFetchedHasTutorMemory(true);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [hasTutorMemoryProp]);

  useEffect(() => {
    if (isTutorMode) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isTutorMode]);

  useEffect(() => {
    if (isOpen && tab === "chat" && isTutorMode) inputRef.current?.focus();
  }, [isOpen, tab, isTutorMode]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, MAX_INPUT_HEIGHT)}px`;
  }, [input, isOpen, tab, isTutorMode]);

  useEffect(() => {
    if (!nudgeAt || nudgeAt === lastNudgeAtRef.current || isOpen || reducedMotion || isAudioPlaying)
      return;
    lastNudgeAtRef.current = nudgeAt;
    setIsNudgeSpinning(true);
  }, [nudgeAt, isOpen, reducedMotion, isAudioPlaying]);

  useEffect(() => {
    const unsubPlay = subscribeLessonAudioPlay(() => {
      if (audioEndTimerRef.current !== null) {
        clearTimeout(audioEndTimerRef.current);
        audioEndTimerRef.current = null;
      }
      setIsAudioPlaying(true);
    });
    const unsubStop = subscribeLessonAudioStop(() => {
      setIsAudioPlaying(false);
      if (!reducedMotion) {
        audioEndTimerRef.current = setTimeout(() => {
          audioEndTimerRef.current = setTimeout(() => {
            audioEndTimerRef.current = null;
            setIsNudgeSpinning(true);
          }, 650);
        }, 60);
      }
    });
    return () => {
      unsubPlay();
      unsubStop();
      if (audioEndTimerRef.current !== null) {
        clearTimeout(audioEndTimerRef.current);
        audioEndTimerRef.current = null;
      }
    };
  }, [reducedMotion]);

  function handleDismiss() {
    setMemoryPanelOpen(false);
    setSessionsPanelOpen(false);
    dismiss();
  }

  function handleNewChat() {
    newSession();
    setSessionsPanelOpen(false);
    setMemoryPanelOpen(false);
    inputRef.current?.focus();
  }

  function handleSend() {
    const text = input.trim();
    if (!text) return;
    sendUserMessage(text);
    setInput("");
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  const isStreaming = messages.some((m) => m.isStreaming);

  const headerStyle = CORAL_HEADER_STYLE;
  const headerTitle = t("common.tutor");

  if (!isOpen) {
    return (
      <div data-agent-widget className="tutor-widget-anchor z-50 flex items-center">
        <div
          aria-hidden={!isAudioPlaying}
          className="tutor-audio-pill mr-2 flex items-center gap-2 overflow-hidden rounded-2xl px-3"
          style={{
            height: "44px",
            background: "var(--surface)",
            boxShadow: "var(--shadow-float), 0 0 0 1px var(--hairline)",
            maxWidth: isAudioPlaying ? "140px" : "0px",
            opacity: isAudioPlaying ? 1 : 0,
            transition: "max-width 0.35s cubic-bezier(0.34,1.56,0.64,1), opacity 0.25s ease",
            pointerEvents: isAudioPlaying ? "auto" : "none",
          }}
        >
          <div className="flex items-center gap-0.5" aria-hidden="true">
            {[0, 80, 160, 80, 0].map((delay, i) => (
              <span
                key={i}
                className="inline-block w-0.5 rounded-full"
                style={{
                  height: "18px",
                  background: "linear-gradient(180deg, var(--coral-light) 0%, var(--coral) 100%)",
                  animation: isAudioPlaying ? `wave 1.1s ease-in-out ${delay}ms infinite` : "none",
                  transform: isAudioPlaying ? undefined : "scaleY(0.4)",
                  transformOrigin: "center",
                }}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={stopLessonAudio}
            aria-label="Pause audio"
            className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full transition-colors hover:bg-black/5"
            style={{ color: "var(--coral)" }}
          >
            <svg viewBox="0 0 16 16" fill="currentColor" className="h-3.5 w-3.5">
              <rect x="3" y="2" width="3.5" height="12" rx="1" />
              <rect x="9.5" y="2" width="3.5" height="12" rx="1" />
            </svg>
          </button>
        </div>

        <button
          type="button"
          onClick={open}
          aria-label="Open tutor chat"
          className="tutor-widget-launcher hover:scale-105 active:scale-95"
          style={{
            filter: isNudgeSpinning ? LAUNCHER_HAZE_HIDDEN : LAUNCHER_HAZE_VISIBLE,
            // The haze drops out quickly when the spin starts, then eases back in
            // shortly after the spin completes instead of snapping.
            transition: isNudgeSpinning
              ? "transform 0.15s ease, filter 0.15s ease"
              : "transform 0.15s ease, filter 0.6s ease 0.15s",
          }}
        >
          <span
            className={isNudgeSpinning ? "tutor-widget-spin block" : "block"}
            onAnimationEnd={() => setIsNudgeSpinning(false)}
          >
            <ConvoIcon className="h-14 w-14" />
          </span>
        </button>
      </div>
    );
  }

  const showDebug = isDev && tab === "debug";

  return (
    <>
      <div
        data-agent-widget
        className={`tutor-widget-anchor tutor-chat-panel z-50 flex w-full flex-col overflow-hidden rounded-none sm:rounded-3xl ${showDebug ? "sm:w-96" : "sm:w-80"}`}
        style={{
          background: "var(--surface)",
          boxShadow: "var(--shadow-float), 0 0 0 1px var(--hairline)",
        }}
        aria-label={showDebug ? "Tutor debug" : "Tutor chat"}
      >
        <div
          className="flex flex-shrink-0 items-center justify-between px-4 py-3"
          style={headerStyle}
        >
          <div className="flex min-w-0 flex-1 items-center gap-2.5">
            <div
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden"
              style={{ borderRadius: "9px 9px 3px 9px", background: "rgba(255,255,255,0.18)" }}
            >
              <ConvoIcon className="h-6 w-6" />
            </div>
            {isDev && isTutorMode ? (
              <div
                className="flex rounded-full p-0.5"
                style={{ background: "rgba(255,255,255,0.15)" }}
                role="tablist"
                aria-label="Tutor panel"
              >
                {(["chat", "debug"] as const).map((panel) => (
                  <button
                    key={panel}
                    type="button"
                    role="tab"
                    aria-selected={tab === panel}
                    onClick={() => setTab(panel)}
                    className="rounded-full px-2.5 py-0.5 font-display text-xs font-semibold capitalize transition-colors"
                    style={{
                      background: tab === panel ? "rgba(255,255,255,0.22)" : "transparent",
                      color: tab === panel ? "white" : "rgba(255,255,255,0.65)",
                    }}
                  >
                    {panel}
                  </button>
                ))}
              </div>
            ) : (
              <span className="truncate font-display text-sm font-semibold text-white">
                {headerTitle}
              </span>
            )}
            {isTutorMode && isStreaming && (
              <span className="flex items-center gap-0.5 pb-0.5">
                {[0, 150, 300].map((delay) => (
                  <span
                    key={delay}
                    className="h-1.5 w-1.5 animate-bounce rounded-full bg-white/60"
                    style={{ animationDelay: `${delay}ms` }}
                  />
                ))}
              </span>
            )}
          </div>
          <div className="ml-2 flex flex-shrink-0 items-center">
            {isTutorMode ? (
              <>
                <button
                  type="button"
                  onClick={handleNewChat}
                  aria-label={t("agent.tutorSessions.new")}
                  className={HEADER_ICON_BUTTON}
                  style={{ color: "rgba(255,255,255,0.75)" }}
                >
                  <NewChatIcon className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSessionsPanelOpen((v) => !v);
                    setMemoryPanelOpen(false);
                  }}
                  aria-label={t("agent.tutorSessions.toggleAria")}
                  aria-expanded={sessionsPanelOpen}
                  aria-controls="tutor-sessions-panel"
                  className={HEADER_ICON_BUTTON}
                  style={{ color: sessionsPanelOpen ? "white" : "rgba(255,255,255,0.75)" }}
                >
                  <HistoryIcon className="h-5 w-5" />
                </button>
              </>
            ) : null}
            {hasTutorMemory ? (
              <button
                type="button"
                onClick={() => {
                  setMemoryPanelOpen((open) => !open);
                  setSessionsPanelOpen(false);
                }}
                aria-label={t("agent.tutorMemory.toggleAria")}
                aria-expanded={memoryPanelOpen}
                aria-controls="tutor-memory-panel"
                className={HEADER_ICON_BUTTON}
                style={{ color: memoryPanelOpen ? "white" : "rgba(255,255,255,0.75)" }}
              >
                <span className="material-symbols-outlined text-[20px]" aria-hidden="true">
                  psychology
                </span>
              </button>
            ) : null}
            <button
              type="button"
              onClick={handleDismiss}
              aria-label="Close tutor chat"
              className="rounded-full p-1.5 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
            >
              <svg viewBox="0 0 16 16" fill="currentColor" className="h-3.5 w-3.5">
                <path d="M4.293 4.293a1 1 0 0 1 1.414 0L8 6.586l2.293-2.293a1 1 0 1 1 1.414 1.414L9.414 8l2.293 2.293a1 1 0 0 1-1.414 1.414L8 9.414l-2.293 2.293a1 1 0 0 1-1.414-1.414L6.586 8 4.293 5.707a1 1 0 0 1 0-1.414Z" />
              </svg>
            </button>
          </div>
        </div>

        {hasTutorMemory && memoryPanelOpen ? (
          <TutorMemoryPanel open={memoryPanelOpen} variant="header" />
        ) : null}

        {isTutorMode ? (
          <TutorSessionsPanel
            open={sessionsPanelOpen}
            sessions={sessions}
            activeSessionId={activeSessionId}
            onSwitch={(id) => {
              void switchSession(id);
              setSessionsPanelOpen(false);
            }}
          />
        ) : null}

        {showDebug ? (
          <AgentChatDebugPanel />
        ) : isTutorMode ? (
          <>
            <div className="flex-1 overflow-y-auto px-3 py-3">
              {messages.length === 0 ? (
                <p
                  className="mt-2 text-center text-xs leading-relaxed"
                  style={{ color: "var(--ink-faint)" }}
                >
                  Ask me anything about this lesson, or I&apos;ll chime in when I notice something
                  useful.
                </p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {messages.map((msg) => (
                    <li
                      key={msg.id}
                      className={
                        msg.role === "user" ? "flex justify-end" : "flex flex-col items-start gap-2"
                      }
                    >
                      <div
                        className="max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed"
                        style={
                          msg.role === "user"
                            ? {
                                background:
                                  "linear-gradient(135deg, var(--coral-light) 0%, var(--coral) 100%)",
                                color: "white",
                                borderBottomRightRadius: "4px",
                              }
                            : {
                                background: "var(--surface-sunk)",
                                color: "var(--ink-body)",
                                borderBottomLeftRadius: "4px",
                                boxShadow: "var(--inset-field)",
                              }
                        }
                      >
                        {msg.text || (msg.isStreaming ? " " : "")}
                        {msg.isStreaming && (
                          <span
                            className="ml-0.5 inline-block h-3.5 w-0.5 animate-pulse align-middle"
                            style={{ background: "var(--ink-faint)" }}
                            aria-hidden="true"
                          />
                        )}
                      </div>
                      {msg.role === "tutor" && msg.action && !msg.isStreaming && (
                        <AgentActionChip action={msg.action} />
                      )}
                    </li>
                  ))}
                  <div ref={messagesEndRef} />
                </ul>
              )}
            </div>

            <div
              className="flex-shrink-0 px-3 pb-3 pt-2"
              style={{ borderTop: "1px solid var(--hairline)" }}
            >
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  rows={1}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask your tutor…"
                  disabled={isStreaming}
                  className="inset-field flex-1 resize-none py-2 text-sm"
                  style={{
                    maxHeight: `${MAX_INPUT_HEIGHT}px`,
                    overflowY: "auto",
                    borderRadius: "12px",
                  }}
                />
                <SendButton onClick={handleSend} disabled={!input.trim() || isStreaming} />
              </div>
              <p className="mt-1.5 text-center text-[10px]" style={{ color: "var(--ink-faint)" }}>
                Enter to send · Shift+Enter for new line
              </p>
            </div>
          </>
        ) : null}
      </div>
    </>
  );
}
